import { useCallback, useState } from 'react';
import { withTranslation, WithTranslation } from 'react-i18next';
import { Redirect, useLocation } from 'react-router-dom';
import {
  Button,
  DataCard,
  Dialog,
  HStack,
  Icon,
  Typography,
} from '@vivaahealth/design-system';

import styles from './review-insurance-page.module.css';

import * as api from '../../../../../api';
import { getInsuranceCardData } from '../utils';

interface ReviewInsurancePageProps extends WithTranslation {
  cards: api.PatientInsuranceInformation.InsuranceInformationWithDetails[];
  payorsDisplayNameMapping: Record<string, string>;
  navigateToNextPage: () => Promise<void>;
  onContinue: () => Promise<void>;
  onRemoveInsurance: (cardId: string) => Promise<void>;
  handleInsuranceChoice: (
    payingWithInsurance: boolean,
    skippingInsurance: boolean,
  ) => Promise<void>;
  onAddInsurance: () => void;
  onEdit: (insuranceInformationId: string) => Promise<void>;
}

const ReviewInsurancePage = ({
  cards,
  payorsDisplayNameMapping,
  navigateToNextPage,
  handleInsuranceChoice,
  onAddInsurance,
  onContinue,
  onEdit,
  onRemoveInsurance,
  t,
}: ReviewInsurancePageProps) => {
  const [showRemoveInsuranceModal, setShowRemoveInsuranceModal] =
    useState(false);
  const [showInvalidInsurancesModal, setShowInvalidInsurancesModal] =
    useState(false);
  const [cardIdToRemove, setCardIdToRemove] = useState('');
  const [showSkipInsurancePopup, setShowSkipInsurancePopup] = useState(false);
  const location = useLocation();
  const hasIncompleteCards = cards.some((card) => !card.completed);

  const skipInsurance = async () => {
    setShowSkipInsurancePopup(false);
    await handleInsuranceChoice(false, true);
    await navigateToNextPage();
  };

  const handleClickConfirmRemoveInsurance = useCallback(async () => {
    await onRemoveInsurance(cardIdToRemove);
    setShowRemoveInsuranceModal(false);
  }, [cardIdToRemove, onRemoveInsurance]);

  if (cards.length === 0) {
    return <Redirect to={'./' + location.search} />;
  }

  return (
    <div className={styles.container}>
      <Typography variant="headline-small" bold>
        {t('title')}
      </Typography>
      <div
        data-testid="insurance-cards-container"
        className={styles['insurance-cards-container']}
      >
        {cards.map((card) => {
          const {
            id,
            insuranceProviderImgSrc,
            memberName,
            memberId,
            groupNumber,
            coverageStartDate,
            payorDisplayName,
          } = getInsuranceCardData(card, payorsDisplayNameMapping);
          const { patientInsuranceInformationId, completed } = card;

          return (
            <div
              className={!completed ? styles['warning-outlined'] : undefined}
            >
              <DataCard
                key={id}
                className="testid-data-card"
                cardHeaderLeft={
                  !!insuranceProviderImgSrc ? (
                    <img
                      src={insuranceProviderImgSrc}
                      alt={t('logoOfInsuranceProvider', { ns: 'insurance' })}
                      height={40}
                    />
                  ) : (
                    <Typography variant="title-medium" bold>
                      {t(payorDisplayName)}
                    </Typography>
                  )
                }
                cardHeaderRight={
                  <HStack gap={8}>
                    {patientInsuranceInformationId && !completed && (
                      <Button
                        data-cy="edit-card"
                        children={t('common::edit')}
                        onClick={async () =>
                          await onEdit(patientInsuranceInformationId)
                        }
                        size="small"
                        variant="text"
                      />
                    )}
                    <Button
                      data-cy="remove-card"
                      children={t('common::remove')}
                      onClick={() => {
                        setCardIdToRemove(card.id);
                        setShowRemoveInsuranceModal(true);
                      }}
                      size="small"
                      variant="text"
                    />
                  </HStack>
                }
                rows={[
                  {
                    rowHeader: t('subscriber', { ns: 'insurance' }),
                    rowData: memberName,
                  },
                  {
                    rowHeader: t('memberId', { ns: 'insurance' }),
                    rowData: memberId,
                  },
                  {
                    rowHeader: t('groupNumber', { ns: 'insurance' }),
                    rowData: groupNumber,
                  },
                  {
                    rowHeader: t('coverageStartDate', { ns: 'insurance' }),
                    rowData: coverageStartDate,
                  },
                ]}
              />
            </div>
          );
        })}
      </div>

      <div className={styles['action-container']}>
        <Button
          fullWidth
          data-testid="confirm-insurance"
          onClick={() => {
            if (hasIncompleteCards) return setShowInvalidInsurancesModal(true);
            return onContinue();
          }}
        >
          {t('confirmInsurance')}
        </Button>
        <Button fullWidth variant="outlined" onClick={onAddInsurance}>
          {t('addAnotherInsurance')}
        </Button>
        <Button
          fullWidth
          variant="plain"
          onClick={() => setShowSkipInsurancePopup(true)}
        >
          {t('doNotUseInsurance', { ns: 'insurance' })}
        </Button>
      </div>

      <Dialog
        className={'testid-remove-insurance'}
        open={showRemoveInsuranceModal}
        Icon={<Icon.AlertTriangle color={'#FFAE00'} size={'large'} />}
        title={t('removeInsuranceModalTitle')}
        description={t('removeInsuranceModalBody')}
      >
        <Button
          fullWidth
          size="medium"
          onClick={handleClickConfirmRemoveInsurance}
        >
          {t('removeInsuranceModalConfirm')}
        </Button>
        <Button
          fullWidth
          variant="outlined"
          size="medium"
          onClick={() => setShowRemoveInsuranceModal(false)}
        >
          {t('goBack', { ns: 'insurance' })}
        </Button>
      </Dialog>

      <Dialog
        open={showInvalidInsurancesModal}
        Icon={<Icon.AlertTriangle color={'#FFAE00'} size={'large'} />}
        title={t('invalidInsurancesModalTitle')}
        description={t('invalidInsurancesModalBody')}
      >
        <Button
          fullWidth
          variant="outlined"
          size="medium"
          onClick={() => setShowInvalidInsurancesModal(false)}
        >
          {t('goBack', { ns: 'insurance' })}
        </Button>
      </Dialog>

      <Dialog
        open={showSkipInsurancePopup}
        Icon={<Icon.AlertTriangle color={'#FFAE00'} size={'large'} />}
        title={t('doNotUseInsuranceQuestion', { ns: 'insurance' })}
        description={t('withoutAddingInsurance')}
      >
        <Button fullWidth size="medium" onClick={skipInsurance}>
          {t('doNotUseInsurance', { ns: 'insurance' })}
        </Button>
        <Button
          fullWidth
          variant="outlined"
          size="medium"
          onClick={() => setShowSkipInsurancePopup(false)}
        >
          {t('goBack', { ns: 'insurance' })}
        </Button>
      </Dialog>
    </div>
  );
};

export default withTranslation(['reviewInsurancePage', 'insurance'])(
  ReviewInsurancePage,
);
